// react core
import React, { useState, useEffect, useCallback } from "react";

// date handling
import dayjs from "dayjs";

// text ticker
import Marquee from "react-fast-marquee";

// material design
import { Typography } from "@mui/material";
import Box from "@mui/material/Box";
import Grow from "@mui/material/Grow";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";

// fonts and colors
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUserTag as iconRiderTag } from "@fortawesome/pro-solid-svg-icons";
import { faUsers as iconRiders } from "@fortawesome/pro-solid-svg-icons";
import { faCoins as iconPrice } from "@fortawesome/pro-solid-svg-icons";
import { faChevronLeft as iconArrowLeft } from "@fortawesome/pro-thin-svg-icons";
import { faChevronRight as iconArrowRight } from "@fortawesome/pro-thin-svg-icons";
import { faPlusCircle as iconIncrement } from "@fortawesome/pro-solid-svg-icons";
import { faMinusCircle as iconDecrement } from "@fortawesome/pro-solid-svg-icons";
import { faRocketLaunch as iconTicking } from "@fortawesome/pro-duotone-svg-icons";
import { faExplosion as iconLaunched } from "@fortawesome/pro-duotone-svg-icons";
import { faTimes as iconClose } from "@fortawesome/pro-solid-svg-icons";
import { faCircle as iconCircle } from "@fortawesome/pro-solid-svg-icons";
import { faCheckCircle as iconTicked } from "@fortawesome/pro-solid-svg-icons";
import { faCheckCircle as iconHolderTicked } from "@fortawesome/pro-thin-svg-icons";
import { faRocketLaunch as iconGoingTicked } from "@fortawesome/pro-solid-svg-icons";
import { faTimesCircle as iconNotTicked } from "@fortawesome/pro-solid-svg-icons";
import { faInfoCircle as iconHelp } from "@fortawesome/pro-solid-svg-icons";
import { faBan as iconDisabled } from "@fortawesome/pro-solid-svg-icons";
import { faArrowRightArrowLeft as iconTransfer } from "@fortawesome/pro-solid-svg-icons";
import { faToggleLargeOff as iconToggleOff } from "@fortawesome/pro-duotone-svg-icons";
import { faToggleLargeOn as iconToggleOn } from "@fortawesome/pro-duotone-svg-icons";

// entzy config and models
import configEntzy from "components/config/ConfigEntzy";
import { UserAvatar } from "components/utils/common/CommonAvatars";
import { ActionBoxButton } from "components/utils/common/CommonButtons";
import { TextTag } from "components/utils/common/CommonTags";
import { ActionAlert } from "components/utils/common/CommonLoaders";

// action hooks
// import { useSwipeable } from "react-swipeable";

export function DatePicker(props) {
  // const options = props.options;
  const groups = props.options.reduce(
    (groups, item) => ({
      ...groups,
      [item.groupBy]: [...(groups[item.groupBy] || []), item],
    }),
    {},
  );
  const optionsDecorate = props.optionsDecorate;
  const optionsDisabled = props.optionsDisabled ? props.optionsDisabled : [];
  const optionsSoldOut =
    optionsDecorate && optionsDecorate.soldOut ? optionsDecorate.soldOut : [];

  const [selectedGroup, setSelectedGroup] = useState(props.options[0].groupBy);

  // const handleGroupChange = (value) => {
  //   setSelectedGroup(value);
  // };
  const handleGroupNext = (out) => {
    const groupKeys = Object.keys(groups);
    const currentIndex = groupKeys.indexOf(selectedGroup);
    // const nextIndex = (currentIndex + 1) % groupKeys.length;
    const nextIndex =
      currentIndex === groupKeys.length - 1 ? currentIndex : currentIndex + 1;
    if (out.monthName) return groupKeys[nextIndex].split(" ")[0];
    if (out.isDisabled) return nextIndex === currentIndex;
    if (out.tickedCount) {
      const count = groups[groupKeys[nextIndex]].filter((option) =>
        props.value.includes(option.value),
      ).length;
      return count > 0 ? ` (${count})` : "";
    }

    setSelectedGroup(groupKeys[nextIndex]);
  };
  const handleGroupPrev = (out) => {
    const groupKeys = Object.keys(groups);
    const currentIndex = groupKeys.indexOf(selectedGroup);
    // const prevIndex = (currentIndex - 1 + groupKeys.length) % groupKeys.length;
    const prevIndex = currentIndex === 0 ? 0 : currentIndex - 1;
    if (out.monthName) return groupKeys[prevIndex].split(" ")[0];
    if (out.isDisabled) return prevIndex === currentIndex;
    if (out.tickedCount) {
      const count = groups[groupKeys[prevIndex]].filter((option) =>
        props.value.includes(option.value),
      ).length;
      return count > 0 ? ` (${count})` : "";
    }
    setSelectedGroup(groupKeys[prevIndex]);
  };
  const handleGroupCurrent = (out) => {
    const groupKeys = Object.keys(groups);
    const currentIndex = groupKeys.indexOf(selectedGroup);
    if (out.monthName) return groupKeys[currentIndex].split(" ")[0];
    if (out.tickedCount) {
      const count = groups[groupKeys[currentIndex]].filter((option) =>
        props.value.includes(option.value),
      ).length;
      return count > 0 ? ` (${count})` : "";
    }
    return "";
  };

  // swipe handlers for module switching
  // const swipeHandlers = useSwipeable({
  //   onSwipedLeft: (e) => handleGroupNext(e),
  //   onSwipedRight: (e) => handleGroupPrev(e),
  // });

  // date status terms
  const dateStatus = {
    launched: {
      display: "Launched",
      description: "Date is live (instant trigger)",
    },
    ticking: {
      display: "Ticking",
      description: "Date has some offers",
    },
    limited: {
      display: "Unavailable",
      description: "Places maxed or offer mismatch",
    },
    maxed: {
      display: "Maxed",
      description: "Date is full",
    },
  };

  return (
    <Box className="box-default">
      <Box
        className="box-default"
        sx={{
          pt: configEntzy.APP_SPACING_MD,
          pb: configEntzy.APP_SPACING_MD2X,
        }}
      >
        <Typography variant="subtitle2">{props.label}</Typography>
        <Typography variant="subtitle1">
          <em>{props.placeholder}</em>
        </Typography>
      </Box>
      <Box
        className="box-default border-glow"
        sx={{
          borderRadius: configEntzy.BORDER_SIZE_XL,
          overflow: "hidden",
        }}
      >
        <Box
          className="box-default"
          sx={{
            //  custom-scrollbar
            // height: configEntzy.AVATAR_CONTAINER_XL2X,
            // overflowY: "scroll",
            // overflowX: "hidden",
            pb: configEntzy.APP_SPACING_MD,
            borderRadius: configEntzy.BORDER_SIZE_XL,
          }}
        >
          <Box className="box-default">
            {Object.entries(groups).map(([header, options], index) => (
              <Box
                key={header}
                className="box-default"
                hidden={selectedGroup !== header}
              >
                <Box
                  className="box-default text-left action-pointer"
                  sx={{
                    width: "50%",
                    pt: configEntzy.APP_SPACING_MD,
                    pl: configEntzy.APP_SPACING_MD,
                    pr: configEntzy.APP_SPACING_XS2X,
                  }}
                >
                  <ActionBoxButton
                    size="small"
                    bgColor="dusk.dark"
                    color="orange"
                    text={
                      <span
                        style={{
                          visibility: handleGroupPrev({ isDisabled: true })
                            ? "hidden"
                            : "visible",
                        }}
                      >
                        &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                        {handleGroupPrev({ monthName: true })}
                        {handleGroupPrev({ tickedCount: true })}
                        &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                      </span>
                    }
                    onClick={handleGroupPrev}
                    disabled={handleGroupPrev({ isDisabled: true })}
                    startIcon={
                      handleGroupPrev({ isDisabled: true }) ? null : (
                        <FontAwesomeIcon
                          icon={iconArrowLeft}
                          transform="grow-8"
                          color="white"
                        />
                      )
                    }
                  />
                </Box>
                <Box
                  className="box-default text-right action-pointer"
                  sx={{
                    width: "50%",
                    pt: configEntzy.APP_SPACING_MD,
                    pr: configEntzy.APP_SPACING_MD,
                    pl: configEntzy.APP_SPACING_XS2X,
                  }}
                >
                  <ActionBoxButton
                    size="small"
                    bgColor="dusk.dark"
                    color="orange"
                    text={
                      <span
                        style={{
                          visibility: handleGroupNext({ isDisabled: true })
                            ? "hidden"
                            : "visible",
                        }}
                      >
                        &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                        {handleGroupNext({ monthName: true })}
                        {handleGroupNext({ tickedCount: true })}
                        &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                      </span>
                    }
                    onClick={handleGroupNext}
                    disabled={handleGroupNext({ isDisabled: true })}
                    endIcon={
                      handleGroupNext({ isDisabled: true }) ? null : (
                        <FontAwesomeIcon
                          icon={iconArrowRight}
                          transform="grow-8"
                          color="white"
                        />
                      )
                    }
                  />
                </Box>
                <Box
                  className="box-default action-pointer"
                  sx={{
                    pt: configEntzy.APP_SPACING_MD,
                    pl: configEntzy.APP_SPACING_MD,
                    pr: configEntzy.APP_SPACING_MD,
                  }}
                >
                  <TextTag
                    text={
                      <span>
                        &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                        {handleGroupCurrent({ monthName: true })}
                        {handleGroupCurrent({ tickedCount: true })}
                        &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                      </span>
                    }
                    size="md"
                    rounded={true}
                    fullWidth={true}
                  />
                </Box>

                {options.map((option, index) => {
                  const ticking = optionsDecorate.ticking.find(
                    (obj) => obj.id === option.id,
                  );
                  const progress = ticking ? ticking.progress : {};
                  const isTicking =
                    optionsDecorate &&
                    optionsDecorate.ticking &&
                    optionsDecorate.ticking.filter(
                      (obj) => obj.id === option.id,
                    ).length > 0;
                  const isLaunched =
                    optionsDecorate &&
                    optionsDecorate.launched &&
                    optionsDecorate.launched.includes(option.id);
                  const isSoldOut =
                    optionsSoldOut && optionsSoldOut.includes(option.id);
                  const isDisabled =
                    optionsDisabled && optionsDisabled.includes(option.id);
                  const isHolderTicked = props.value.includes(option.value);
                  const isOtherHolderTick =
                    optionsDecorate &&
                    optionsDecorate.otherHolderTicks &&
                    optionsDecorate.otherHolderTicks.includes(option.id);
                  const isLaunchable =
                    progress.trigger &&
                    progress.trigger.total >= 100 &&
                    !isLaunched &&
                    !isSoldOut &&
                    !isDisabled;
                  const date = dayjs(option.id, "YYYY-MM-DD");
                  const daysToDate = dayjs(date).diff(dayjs(), "day");
                  return (
                    <Box
                      className="box-default"
                      key={option.value}
                      sx={{
                        pt: configEntzy.APP_SPACING_MD,
                        pl: configEntzy.APP_SPACING_MD,
                        pr: configEntzy.APP_SPACING_MD,
                      }}
                    >
                      <Box
                        className="box-default shadow-default action-pointer"
                        sx={{
                          backgroundColor: isLaunched
                            ? "primary.dark"
                            : isHolderTicked
                              ? "darkgreen"
                              : isOtherHolderTick
                                ? "lightgreen"
                                : isDisabled && isLaunched
                                  ? "primaryDisabled.dark"
                                  : isDisabled && isTicking
                                    ? "primaryDisabled.light"
                                    : isTicking
                                      ? "secondary.light"
                                      : ["Sat", "Sun"].includes(
                                            option.display.split("|")[1],
                                          )
                                        ? "white"
                                        : "white",
                          borderRadius: configEntzy.BORDER_SIZE_XL,
                          color: isLaunched
                            ? "white"
                            : isHolderTicked
                              ? "white"
                              : isOtherHolderTick
                                ? "black"
                                : isDisabled && isLaunched
                                  ? "white"
                                  : isDisabled && isTicking
                                    ? "white"
                                    : isTicking
                                      ? "black"
                                      : ["Sat", "Sun"].includes(
                                            option.display.split("|")[1],
                                          )
                                        ? "black"
                                        : "black",
                        }}
                        onClick={
                          isDisabled && !(isHolderTicked && !isLaunched)
                            ? undefined
                            : () =>
                                props.onChange(
                                  {
                                    target: {
                                      id: props.id,
                                      name: props.name,
                                      value: option.value,
                                      display: option.display,
                                    },
                                  },
                                  option.value,
                                  "multi-select",
                                )
                        }
                      >
                        <Box
                          className={
                            isDisabled
                              ? "box-default bg-black-t50"
                              : "box-default"
                          }
                          sx={{
                            p: configEntzy.APP_SPACING_MD,
                            borderRadius: configEntzy.BORDER_SIZE_XL,
                          }}
                        >
                          <Box className="box-default">
                            <Box className="box-default half-width text-left">
                              <Typography variant="subtitle2">
                                <span>{date.format("ddd DD MMM YYYY")}</span>
                              </Typography>
                            </Box>
                            <Box className="box-default half-width text-right">
                              {!isHolderTicked && !isDisabled && (
                                <Box className="box-inline right">
                                  <Typography variant="subtitle2" noWrap>
                                    <span>
                                      <span>&nbsp;&nbsp;&nbsp;&nbsp;</span>
                                      <FontAwesomeIcon
                                        icon={iconToggleOff}
                                        transform="grow-12"
                                        color="black"
                                      />
                                      <span>&nbsp;&nbsp;</span>
                                    </span>
                                  </Typography>
                                </Box>
                              )}
                              {isHolderTicked && (
                                <Box className="box-inline right">
                                  <Typography variant="subtitle2" noWrap>
                                    {isLaunched ? (
                                      <TextTag
                                        size="sm"
                                        bgColor="darkgreen"
                                        color="white"
                                        rounded={true}
                                        text={
                                          <span>
                                            <span>&nbsp;</span>
                                            {isDisabled ? (
                                              <span>Matched</span>
                                            ) : (
                                              <span>Instant Match!</span>
                                            )}
                                            <span>&nbsp;&nbsp;</span>
                                            <FontAwesomeIcon
                                              icon={iconGoingTicked}
                                              color="lightgreen"
                                            />
                                            <span>
                                              &nbsp;&nbsp;&nbsp;&nbsp;
                                            </span>
                                            <FontAwesomeIcon
                                              icon={iconToggleOn}
                                              transform="grow-8"
                                              color="white"
                                            />
                                            <span>&nbsp;</span>
                                          </span>
                                        }
                                      />
                                    ) : (
                                      <span>
                                        <span>OFFER</span>
                                        <span>&nbsp;&nbsp;</span>
                                        <FontAwesomeIcon
                                          icon={iconTicked}
                                          transform="grow-2"
                                          color="white"
                                        />
                                        <span>&nbsp;&nbsp;&nbsp;&nbsp;</span>
                                        <FontAwesomeIcon
                                          icon={iconToggleOn}
                                          transform="grow-12"
                                          color="white"
                                        />
                                        <span>&nbsp;&nbsp;</span>
                                      </span>
                                    )}
                                  </Typography>
                                </Box>
                              )}
                              {!isHolderTicked && isOtherHolderTick && (
                                <Box className="box-inline right">
                                  <Typography variant="subtitle2" noWrap>
                                    <span>&nbsp;&nbsp;</span>
                                    <span>Holder Tick</span>
                                    <span>&nbsp;&nbsp;</span>
                                    <span className="fa-layers fa-fw">
                                      <FontAwesomeIcon
                                        icon={iconCircle}
                                        transform="grow-2"
                                        color="white"
                                      />
                                      <FontAwesomeIcon
                                        icon={iconHolderTicked}
                                        transform="grow-4"
                                        color="green"
                                      />
                                    </span>
                                  </Typography>
                                </Box>
                              )}
                              {isDisabled && (
                                <Box className="box-inline right">
                                  <Typography variant="subtitle2" noWrap>
                                    <span>&nbsp;&nbsp;</span>
                                    <span>Locked</span>
                                    <span>&nbsp;&nbsp;</span>
                                    <span className="fa-layers fa-fw">
                                      <FontAwesomeIcon
                                        icon={iconCircle}
                                        color="white"
                                      />
                                      <FontAwesomeIcon
                                        icon={iconDisabled}
                                        color="red"
                                      />
                                    </span>
                                  </Typography>
                                </Box>
                              )}
                            </Box>
                          </Box>
                          {isLaunched ? (
                            <Box
                              className="box-default"
                              sx={{
                                pt: configEntzy.APP_SPACING_SM,
                                borderRadius: configEntzy.BORDER_SIZE_XL,
                                // backgroundColor: isSoldOut
                                //   ? "primaryDisabled.dark"
                                //   : "primary.dark",
                                opacity: 0.8,
                                whiteSpace: "nowrap",
                              }}
                            >
                              <Box className="box-default">
                                <Typography variant="subtitle2" color="white">
                                  <span style={{ float: "left" }}>
                                    {isSoldOut ? (
                                      <span>
                                        <span>Live</span>
                                        <span>&nbsp;</span>
                                        {progress.remaining.capacity > 0
                                          ? dateStatus.limited.display
                                          : dateStatus.maxed.display}
                                      </span>
                                    ) : (
                                      <span>{daysToDate} days to go</span>
                                    )}
                                  </span>

                                  <span style={{ float: "right" }}>
                                    <span>&nbsp;</span>
                                    <span>
                                      {progress.remaining.capacity} places left
                                    </span>
                                    <span>&nbsp;</span>
                                  </span>
                                </Typography>
                              </Box>
                              <Box
                                className="box-default bg-white-t50 shadow-dark"
                                sx={{
                                  mt: configEntzy.APP_SPACING_SM,
                                  p: configEntzy.APP_SPACING_SM,
                                  borderRadius: configEntzy.BORDER_SIZE_XL,
                                }}
                              >
                                <Typography variant="subtitle2" color="black">
                                  <span style={{ float: "left" }}>
                                    <span>&nbsp;</span>
                                    {isSoldOut ? (
                                      <span>
                                        {progress.remaining.capacity > 0
                                          ? dateStatus.limited.description
                                          : dateStatus.maxed.description}
                                      </span>
                                    ) : (
                                      <span>Launched</span>
                                    )}
                                    <span>&nbsp;</span>
                                  </span>
                                  <span>&nbsp;</span>
                                  <span style={{ float: "right" }}>
                                    <span>&nbsp;</span>
                                    <FontAwesomeIcon icon={iconLaunched} />
                                    <span>&nbsp;</span>
                                  </span>
                                </Typography>
                              </Box>
                            </Box>
                          ) : isTicking ? (
                            <Box
                              className="box-default"
                              sx={{
                                pt: configEntzy.APP_SPACING_SM,
                                borderRadius: configEntzy.BORDER_SIZE_XL,
                                // backgroundColor: isSoldOut
                                //   ? "primaryDisabled.light"
                                //   : "secondary.light",
                                opacity: 0.8,
                              }}
                            >
                              <Box className="box-default">
                                <Typography variant="subtitle2" noWrap>
                                  <span style={{ float: "left" }}>
                                    <span>Runner&nbsp;Offer&nbsp;</span>
                                    {option.selected.runners ? (
                                      <FontAwesomeIcon icon={iconTicked} />
                                    ) : (
                                      <FontAwesomeIcon icon={iconNotTicked} />
                                    )}
                                  </span>
                                  <span style={{ float: "right" }}>
                                    <span>{progress.count}</span>
                                    {progress.remaining.trigger > 0 && (
                                      <span>
                                        /
                                        {progress.count +
                                          progress.remaining.trigger}
                                      </span>
                                    )}
                                    <span>&nbsp;</span>
                                    {progress.count === 1 &&
                                    progress.remaining.trigger === 0
                                      ? "Rider Offer"
                                      : "Rider Offers"}
                                  </span>
                                </Typography>
                              </Box>
                              <Box
                                className="box-default text-right nooverflow"
                                sx={{
                                  mt: configEntzy.APP_SPACING_SM,
                                  backgroundColor: "black",
                                  borderRadius: configEntzy.BORDER_SIZE_XL,
                                }}
                              >
                                <Box
                                  className="box-default"
                                  sx={{
                                    width: progress.trigger.total + "%",
                                    pt: configEntzy.APP_SPACING_SM,
                                    pb: configEntzy.APP_SPACING_SM,
                                    backgroundColor: isSoldOut
                                      ? "primaryDisabled.main"
                                      : "primary.main",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  <Typography variant="subtitle2" color="white">
                                    {isLaunchable && (
                                      <Marquee speed={20} gradient={false}>
                                        <span>
                                          <span>&nbsp;&nbsp;</span>
                                          <span>
                                            Launchable! Needs Run Team to Match
                                          </span>
                                        </span>
                                      </Marquee>
                                    )}
                                    {progress.trigger.total > 50 &&
                                      !isLaunchable && (
                                        <span>
                                          <span>&nbsp;&nbsp;</span>
                                          {isSoldOut ? (
                                            <span>
                                              {dateStatus.limited.description}
                                            </span>
                                          ) : (
                                            <span>
                                              <span>
                                                {progress.trigger.total + "%"}
                                              </span>
                                            </span>
                                          )}
                                          <span>&nbsp;</span>
                                        </span>
                                      )}
                                    {progress.trigger.total > 0 &&
                                      !isLaunchable && (
                                        <span>
                                          <span>&nbsp;&nbsp;</span>
                                          <FontAwesomeIcon
                                            icon={iconTicking}
                                            transform="rotate-45"
                                          />
                                          <span>&nbsp;&nbsp;</span>
                                        </span>
                                      )}
                                  </Typography>
                                </Box>
                                <Box
                                  className="box-default"
                                  sx={{
                                    width: 100 - progress.trigger.total + "%",
                                    pt: configEntzy.APP_SPACING_SM,
                                    pb: configEntzy.APP_SPACING_SM,
                                    whiteSpace: "nowrap",
                                  }}
                                  hidden={progress.trigger.total > 50}
                                >
                                  <Typography
                                    variant="subtitle2"
                                    color="orange"
                                  >
                                    <span
                                      style={{ float: "right", color: "white" }}
                                    >
                                      <span>&nbsp;&nbsp;</span>
                                      {isSoldOut ? (
                                        <span>
                                          {dateStatus.limited.description}
                                        </span>
                                      ) : (
                                        <span>
                                          {progress.trigger.total + "%"}
                                        </span>
                                      )}
                                      <span>&nbsp;&nbsp;</span>
                                    </span>
                                  </Typography>
                                </Box>
                              </Box>
                            </Box>
                          ) : (
                            <Box
                              className="box-default text-left"
                              sx={{
                                pt: configEntzy.APP_SPACING_SM,
                                borderRadius: configEntzy.BORDER_SIZE_XL,
                              }}
                            >
                              <Box className="box-default">
                                <Typography
                                  variant="subtitle2"
                                  className="content-disabled"
                                >
                                  {isSoldOut ? (
                                    <span>
                                      {dateStatus.limited.description}
                                    </span>
                                  ) : (
                                    <em>No offers yet</em>
                                  )}
                                </Typography>
                              </Box>
                              <Box
                                className="box-default"
                                sx={{
                                  mt: configEntzy.APP_SPACING_SM,
                                }}
                                hidden={isDisabled}
                              >
                                <Typography variant="subtitle2">
                                  Be the first to get this date ticking
                                </Typography>
                              </Box>
                            </Box>
                          )}
                        </Box>
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            ))}
          </Box>

          <Box
            className="box-default text-left"
            sx={{ p: configEntzy.APP_SPACING_MD }}
          >
            <Box
              className="box-default"
              sx={{
                p: configEntzy.APP_SPACING_SM,
                mt: configEntzy.APP_SPACING_MD,
              }}
            >
              <Typography variant="subtitle2">
                <FontAwesomeIcon icon={iconHelp} size="2x" color="orange" />
              </Typography>
              <Typography
                variant="subtitle2"
                sx={{
                  mt: configEntzy.APP_SPACING_SM,
                }}
              >
                Date Information
              </Typography>
              <Typography variant="subtitle2">
                Dates offers are limited to 1 year into the future
              </Typography>
              <Typography
                variant="subtitle2"
                color="primary"
                sx={{
                  mt: configEntzy.APP_SPACING_MD,
                }}
              >
                <u>Date Statuses</u>
              </Typography>
              {
                // loop through date status to show as help key
                Object.entries(dateStatus).map(([key, value]) => (
                  <Typography variant="subtitle2" key={key}>
                    <span style={{ color: "orange" }}>{value.display}</span>
                    <span style={{ color: "grey" }}>
                      &nbsp;&nbsp;=&nbsp;&nbsp;
                    </span>
                    <span style={{ color: "white" }}>{value.description}</span>
                    <span style={{ color: "grey" }}>&nbsp;&nbsp;&nbsp;</span>
                  </Typography>
                ))
              }
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

export function MemberPickerNumberRocker(props) {
  const handleOnChange = props.onChange;
  // const ticketTypeForm = props.formState.find(
  //   (item) => item.id === "ticket-type"
  // );
  // const ticketGroupForm = props.formState.find(
  //   (item) => item.id === "ticket-group"
  // );
  // const ticketPriceTotal = props.ticket
  //   ? props.ticket.totalAmount
  //   : ticketTypeForm
  //   ? ticketTypeForm.value.price
  //   : 0;
  // const ticketGroupTotal = props.ticket
  //   ? props.ticket.groupSize
  //   : ticketGroupForm
  //   ? ticketGroupForm.value.value
  //   : 0;

  const ticketOwnerId = props.ticket.UserId;
  const ticketPriceTotal = props.ticket ? props.ticket.TotalAmount : 0;
  const ticketGroupTotal = props.ticket ? props.ticket.Quantity : 0;
  const ticketShareholders = props.ticket ? props.ticket.Shareholders : [];

  // build initial totals from shareholders
  let initialUserMap = {};
  let initialTotals = {};
  let initialTicketNamedTotal = 0;
  ticketShareholders.forEach((shareholder) => {
    const identity = shareholder.UserId;
    const name = shareholder.UserName;
    const total = shareholder.PendingQuantity
      ? shareholder.PendingQuantity
      : shareholder.Quantity;
    if (identity !== ticketOwnerId) {
      initialUserMap[identity] = {
        identity: identity,
        name: name,
      };
      initialTotals[identity] = total;
      initialTicketNamedTotal += total;
      if (initialTicketNamedTotal > ticketGroupTotal) {
        initialTicketNamedTotal = ticketGroupTotal;
      }
    }
  });

  // const [submitted, setSubmitted] = useState(false);
  // const [addNamedGuests, setAddNamedGuests] = useState(false);
  const [sharePayment, setSharePayment] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [ticketNamedTotal, setTicketNamedTotal] = useState(
    initialTicketNamedTotal,
  );
  const [totals, setTotals] = useState(initialTotals);
  const [userMap, setUserMap] = useState(initialUserMap);
  const [recalculate, setRecalculate] = useState(1);
  const [alert, setAlert] = useState(false);
  const [message, setMessage] = useState("");

  // const handleAddNamedGuests = () => {
  //   // setSubmitted(false);
  //   setAddNamedGuests(!addNamedGuests);
  // };
  const handleSharePayment = () => {
    setSharePayment(!sharePayment);
    setRecalculate(recalculate + 1);
  };
  const handleShowHelp = () => {
    setShowHelp(!showHelp);
  };

  const handleIncrement = (option) => {
    if (ticketNamedTotal < ticketGroupTotal - 1) {
      setTotals({
        ...totals,
        [option.identity]: totals[option.identity]
          ? totals[option.identity] + 1
          : 1,
      });
      setTicketNamedTotal(ticketNamedTotal + 1);
      setUserMap({
        ...userMap,
        [option.identity]: option,
      });
      setRecalculate(recalculate + 1);
    }
  };
  const handleDecrement = (option) => {
    setTotals({
      ...totals,
      [option.identity]: totals[option.identity]
        ? totals[option.identity] === 0
          ? 0
          : totals[option.identity] - 1
        : 0,
    });
    setTicketNamedTotal(
      totals[option.identity]
        ? totals[option.identity] === 0
          ? ticketNamedTotal
          : ticketNamedTotal - 1
        : ticketNamedTotal,
    );
    setRecalculate(recalculate + 1);
  };

  const submitResults = () => {
    const results = {};
    results.named = Object.entries(userMap).length
      ? Object.entries(totals).map(([key, value]) => {
          return { identity: key, name: userMap[key].name, total: value };
        })
      : [];
    results.totals = {
      named: ticketNamedTotal,
      unnamed: ticketGroupTotal - ticketNamedTotal,
      group: ticketGroupTotal,
    };
    results.sharePayment = sharePayment;
    // setAddNamedGuests(false);
    // setSubmitted(true);
    if (ticketNamedTotal + ticketGroupTotal === 0) {
      setAlert(true);
      setMessage("Please select at a group size of at least 1");
      return;
    } else {
      setAlert(false);
      setMessage("");
      return handleOnChange(
        null,
        {
          id: "custom-member-data",
          value: results,
          name: "Member Data",
          display: "Member Data",
          icon: null,
        },
        "submitCustomSelect",
      );
    }
  };
  const cbSubmitResults = useCallback(submitResults, [
    ticketNamedTotal,
    ticketGroupTotal,
    sharePayment,
    totals,
    userMap,
    handleOnChange,
  ]);

  // trigger effect to run submit results when recalculate changes
  useEffect(() => {
    if (recalculate > 0) {
      setRecalculate(0);
      cbSubmitResults();
    }
  }, [recalculate, cbSubmitResults]);

  // reset form when ticket group changes
  // useEffect(() => {
  //   setTotals({});
  //   setTicketNamedTotal(0);
  //   setAddNamedGuests(false);
  //   setSharePayment(false);
  //   setSubmitted(false);
  //   setAlert(false);
  //   setMessage("");
  // }, [ticketPriceTotal, ticketGroupTotal]);

  return (
    <Box
      className="box-default"
      sx={{
        pb: configEntzy.APP_SPACING_MD3X,
        borderBottom: "1px solid #999",
      }}
    >
      <Box className="box-default">
        <Typography variant="subtitle2" color="magenta">
          <FontAwesomeIcon icon={iconRiderTag} size="2x" />
        </Typography>
      </Box>
      <Box className="box-default">
        <Typography
          variant="subtitle2"
          color="primary"
          sx={{ mt: configEntzy.APP_SPACING_MD }}
        >
          {props.label}
        </Typography>
      </Box>
      {ticketGroupTotal === 1 ? (
        <Box className="box-default">
          <Typography variant="subtitle1">
            <em>Ticket holder is You</em>
          </Typography>
        </Box>
      ) : (
        <Box className="box-default">
          <Box className="box-default action-pointer" onClick={handleShowHelp}>
            <Typography variant="subtitle1" color="secondary">
              You can allocate riders or leave all in your name
            </Typography>
            <Typography variant="subtitle1">
              <span>Named riders will need to accept.</span>
              <span>&nbsp;More Info&nbsp;</span>
              <FontAwesomeIcon icon={showHelp ? iconClose : iconHelp} />
            </Typography>
            <Typography variant="subtitle1">{props.placeholder}</Typography>
          </Box>
          <Box
            className="box-default"
            sx={{
              mt: configEntzy.APP_SPACING_MD,
            }}
          >
            <Grow in={showHelp} timeout={500}>
              <Box
                className="box-default bg-black-t75 text-left"
                sx={{
                  p: configEntzy.APP_SPACING_MD,
                  mb: configEntzy.APP_SPACING_MD,
                  borderRadius: configEntzy.BORDER_SIZE_XL,
                }}
                hidden={!showHelp}
              >
                <Typography variant="subtitle2" color="primary">
                  <FontAwesomeIcon icon={iconHelp} />
                  <span>&nbsp;&nbsp;</span>
                  Named Riders in Groups
                </Typography>
                <Typography
                  variant="subtitle1"
                  color="white"
                  sx={{
                    mt: configEntzy.APP_SPACING_MD,
                  }}
                >
                  Within a grouped ticket holding you can allocate places to
                  named riders by selecting from your contacts. This means they
                  will be able to access the ticket in their Entzy account,
                  contribute to date offers and show the ticket separately at
                  the point of entry.
                </Typography>
                <Typography
                  variant="subtitle1"
                  color="white"
                  sx={{
                    mt: configEntzy.APP_SPACING_MD,
                  }}
                >
                  Alternatively if you leave place holdings in your name then
                  you control date offers and the other riders must be present
                  with you as a group when showing tickets for entry.
                </Typography>
                <Typography
                  variant="subtitle1"
                  color="white"
                  sx={{
                    mt: configEntzy.APP_SPACING_MD,
                  }}
                >
                  <span style={{ color: "orange" }}>Note:</span> If you allocate
                  named riders then date matching will happen between all of you
                  automatically. Only when everyone aligns on the same dates is
                  an overall offer presented to the run team.
                </Typography>
                <Typography
                  variant="subtitle1"
                  color="white"
                  sx={{
                    mt: configEntzy.APP_SPACING_MD,
                  }}
                >
                  Tap the edit icon to easily add or remove named riders from
                  your group. Contacts will be notified to accept ownership of
                  their holding. For chargeable tickets you have the additional
                  option to split the price on launch.
                </Typography>
              </Box>
            </Grow>
          </Box>
          <Box
            className="box-default shadow-light"
            sx={{
              p: configEntzy.APP_SPACING_MD,
              borderRadius: configEntzy.BORDER_SIZE_XL,
            }}
            // onClick={handleAddNamedGuests}
          >
            <Box className="box-default">
              <Box
                className="box-default upper-case"
                sx={{
                  width: "25%",
                  // pr: configEntzy.APP_SPACING_MD,
                }}
              >
                <Typography variant="body2">In Your</Typography>
                <Typography variant="body2">Name</Typography>
              </Box>
              <Box
                className="box-default"
                sx={{
                  width: "50%",
                  pt: configEntzy.APP_SPACING_SM,
                }}
              >
                <Typography variant="h6" color="primary">
                  <span>&nbsp;&nbsp;</span>
                  <span
                    style={{
                      backgroundColor:
                        ticketGroupTotal - ticketNamedTotal === 0
                          ? "grey"
                          : "orange",
                      color: "white",
                      padding: "2px",
                      borderRadius: "5px",
                    }}
                  >
                    &nbsp;{ticketGroupTotal - ticketNamedTotal}&nbsp;
                  </span>
                  <span>&nbsp;&nbsp;&nbsp;&nbsp;</span>
                  <FontAwesomeIcon icon={iconTransfer} />
                  <span>&nbsp;&nbsp;&nbsp;&nbsp;</span>
                  <span
                    style={{
                      backgroundColor:
                        ticketNamedTotal === 0 ? "grey" : "orange",
                      color: "white",
                      padding: "2px",
                      borderRadius: "5px",
                    }}
                  >
                    &nbsp;{ticketNamedTotal}&nbsp;
                  </span>
                  <span>&nbsp;&nbsp;</span>
                </Typography>
              </Box>

              <Box
                className="box-default upper-case"
                sx={{
                  width: "25%",
                  // pl: configEntzy.APP_SPACING_MD,
                }}
              >
                <Box className="box-default">
                  <Typography variant="body2">Named</Typography>
                  <Typography variant="body2">Riders</Typography>
                </Box>
              </Box>
            </Box>
            {/* <Box
          className="box-default half-width text-right"
          sx={{
            pr: configEntzy.APP_SPACING_MD2X,
          }}
        >
          <Box className="box-default">
            <Box className="box-inline right">
              <UserAvatar size="sm" user={props.user} nameHideInfo={true} />
            </Box>
          </Box>
        </Box>
        <Box
          className="box-default half-width text-left"
          sx={{
            pl: configEntzy.APP_SPACING_MD2X,
            borderLeft: "1px solid #ccc",
          }}
        >
          <Box className="box-default">
            <TextTag
              size="lg"
              text={
                <span>
                  <FontAwesomeIcon icon={iconRiderTag} size="2x" />
                  <span>&nbsp;</span>
                  {ticketNamedTotal}
                  <span>&nbsp;</span>
                </span>
              }
              bgColor={
                ticketNamedTotal === 0 ? "tertiary.dark" : "primary.dark"
              }
              color="white"
              rounded={true}
            />
          </Box>
          <Box
            className="box-default"
            sx={{
              pt: configEntzy.APP_SPACING_SM,
            }}
          >
            <Typography variant="subtitle1">
              <em>Named Riders</em>
            </Typography>
          </Box>
        </Box> */}
          </Box>

          <Box
            className="box-default"
            sx={{
              pt: configEntzy.APP_SPACING_MD,
            }}
            // hidden={!addNamedGuests}
          >
            <Box
              className="box-default shadow-light"
              hidden={ticketPriceTotal === 0}
              sx={{
                mb: configEntzy.APP_SPACING_MD,
                borderRadius: configEntzy.BORDER_SIZE_XL,
              }}
            >
              <Box
                className="box-default"
                sx={{
                  pt: configEntzy.APP_SPACING_MD3X,
                }}
              >
                <Typography variant="subtitle2">
                  <FontAwesomeIcon
                    icon={iconPrice}
                    size="2x"
                    transform="up-10"
                  />
                </Typography>
                <Typography variant="subtitle2">
                  Should each rider also be charged
                </Typography>
                <Typography variant="subtitle2">
                  their share of ticket price on launch?
                </Typography>
              </Box>
              <Box
                className="box-default"
                sx={{
                  pl: configEntzy.APP_SPACING_MD,
                  pb: configEntzy.APP_SPACING_MD,
                  mb: configEntzy.APP_SPACING_SM,
                }}
              >
                <FormControlLabel
                  control={<Switch />}
                  id="share-payment"
                  name="share-payment"
                  label={"Split Charges" + (sharePayment ? " ACTIVE" : "")}
                  checked={sharePayment}
                  onChange={handleSharePayment}
                  labelPlacement="end"
                />
              </Box>
            </Box>

            <Box
              className="box-default bg-black-t10 shadow-light nooverflow"
              sx={{
                p: configEntzy.APP_SPACING_XS2X,
                borderRadius: configEntzy.BORDER_SIZE_XL,
              }}
            >
              <Box
                className="box-default custom-scrollbar"
                sx={{
                  height: configEntzy.AVATAR_CONTAINER_XL2X,
                  p: configEntzy.APP_SPACING_MD,
                  borderRadius: configEntzy.BORDER_SIZE_XL,
                }}
              >
                <Box
                  className="box-default text-paragraph"
                  sx={{
                    pt: configEntzy.APP_SPACING_MD3X,
                  }}
                >
                  <Typography variant="subtitle2">
                    <FontAwesomeIcon
                      icon={iconRiders}
                      size="2x"
                      transform="up-10"
                    />
                  </Typography>
                  <Typography variant="subtitle2">
                    Select riders from your contacts to add to this offer
                  </Typography>
                </Box>
                <Box
                  className="box-default"
                  sx={{
                    pt: configEntzy.APP_SPACING_MD2X,
                  }}
                >
                  {props.options.length ? (
                    props.options.map((option, index) => (
                      <Box
                        key={option.identity}
                        className="box-inline bg-black shadow-purple nooverflow"
                        sx={{
                          width: configEntzy.AVATAR_CONTAINER_MD,
                          mr: configEntzy.APP_SPACING_SM2X,
                          mb: configEntzy.APP_SPACING_SM2X,
                          pt: configEntzy.APP_SPACING_MD,
                          borderRadius: configEntzy.BORDER_SIZE_XL,
                          color: "white",
                        }}
                      >
                        <Box className="box-inline">
                          <UserAvatar user={option} />
                        </Box>
                        <Box
                          className="box-default"
                          sx={{
                            pt: configEntzy.APP_SPACING_MD,
                            pb: configEntzy.APP_SPACING_MD,
                          }}
                        >
                          <Box
                            className="box-default quarter-width text-right action-pointer"
                            onClick={() => {
                              handleDecrement(option);
                            }}
                          >
                            <Typography variant="h6">
                              <FontAwesomeIcon icon={iconDecrement} />
                            </Typography>
                          </Box>
                          <Box className="box-default half-width">
                            <Typography variant="h6">
                              {totals[option.identity] ? (
                                <span
                                  style={{
                                    backgroundColor: "orange",
                                    color: "white",
                                    padding: "2px",
                                    borderRadius: "5px",
                                  }}
                                >
                                  &nbsp;{totals[option.identity]}&nbsp;
                                </span>
                              ) : (
                                0
                              )}
                            </Typography>
                          </Box>
                          <Box
                            className="box-default quarter-width text-left action-pointer"
                            onClick={() => {
                              handleIncrement(option);
                            }}
                          >
                            <Typography variant="h6">
                              <FontAwesomeIcon icon={iconIncrement} />
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    ))
                  ) : (
                    <Box
                      className="box-default"
                      sx={{
                        p: configEntzy.APP_SPACING_MD,
                      }}
                    >
                      <Typography variant="subtitle2">
                        <em>No riders available for selection</em>
                      </Typography>
                      <Typography variant="subtitle2">
                        <em>To update add people to your contacts</em>
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            </Box>

            <Box
              className="box-default text-paragraph content-disabled"
              sx={{
                pt: configEntzy.APP_SPACING_MD,
              }}
            >
              <Typography variant="subtitle2">
                Add members to your contacts if you need to update the list of
                available riders
              </Typography>
            </Box>
          </Box>
        </Box>
      )}

      {alert && message && (
        <Box
          className="box-default"
          sx={{
            p: configEntzy.APP_SPACING_MD,
          }}
        >
          <ActionAlert severity="error" message={message} />
        </Box>
      )}
    </Box>
  );
}
